/**
 * UserAuditPage.jsx — DB-backed User Audit Trail viewer.
 * 100% in-app managed. Reads from WebApp.UserAuditLog.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Shield, RefreshCw, Download, Search, AlertTriangle, CheckCircle, Filter, Users, Activity, Clock, XCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { API } from '../lib/api';


const ACTION_COLORS = {
  LOGIN:                   { bg: 'var(--accent-blue-soft)',   text: 'var(--accent-blue)',  label: 'Login' },
  LOGIN_FAIL:              { bg: 'rgba(239,68,68,.12)',        text: '#ef4444',             label: 'Login Fail' },
  LOGOUT:                  { bg: 'rgba(100,116,139,.12)',      text: '#64748b',             label: 'Logout' },
  LICENSE_GENERATED:       { bg: 'rgba(34,197,94,.12)',        text: '#22c55e',             label: 'License Gen' },
  LICENSE_RENEWED:         { bg: 'rgba(34,197,94,.12)',        text: '#22c55e',             label: 'Renewal' },
  LICENSE_VOIDED:          { bg: 'rgba(239,68,68,.12)',        text: '#ef4444',             label: 'Voided' },
  LICENSE_REVOKED:         { bg: 'rgba(239,68,68,.12)',        text: '#ef4444',             label: 'Revoked' },
  SIGNUP_INITIATED:        { bg: 'rgba(99,102,241,.12)',       text: '#6366f1',             label: 'Signup Start' },
  SIGNUP_COMPLETED:        { bg: 'rgba(34,197,94,.18)',        text: '#16a34a',             label: 'Signup Done' },
  ACCOUNT_ACTIVATED:       { bg: 'rgba(34,197,94,.18)',        text: '#16a34a',             label: 'Activated' },
  ACTIVATION_RESENT:       { bg: 'rgba(251,146,60,.12)',       text: '#fb923c',             label: 'Resend Email' },
  CREDENTIALS_EMAIL_SENT:  { bg: 'rgba(59,130,246,.12)',       text: '#3b82f6',             label: 'Creds Email' },
  TENANT_PASSWORD_RESET:   { bg: 'rgba(239,68,68,.16)',        text: '#dc2626',             label: 'Pwd Reset' },
  SESSION_FORCE_LOGOUT:    { bg: 'rgba(239,68,68,.12)',        text: '#ef4444',             label: 'Force Logout' },
  PRICING_TIER_CREATED:    { bg: 'rgba(168,85,247,.12)',       text: '#a855f7',             label: 'Pricing+' },
  PRICING_TIER_DELETED:    { bg: 'rgba(239,68,68,.12)',        text: '#ef4444',             label: 'Pricing✕' },
  DOWNLOAD_WEBPAGE_PACK:   { bg: 'rgba(20,184,166,.12)',       text: '#14b8a6',             label: 'ZIP Download' },
  DOWNLOAD_ERP_PACK:       { bg: 'rgba(20,184,166,.12)',       text: '#14b8a6',             label: 'ERP ZIP' },
  INDUSTRY_CREATED:        { bg: 'rgba(168,85,247,.12)',       text: '#a855f7',             label: 'Industry+' },
  INDUSTRY_UPDATED:        { bg: 'rgba(168,85,247,.12)',       text: '#a855f7',             label: 'Industry~' },
  INDUSTRY_DEACTIVATED:    { bg: 'rgba(250,173,20,.12)',       text: '#faad14',             label: 'Industry-' },
  INDUSTRY_DELETED:        { bg: 'rgba(239,68,68,.12)',        text: '#ef4444',             label: 'Industry✕' },
  MODULE_CREATED:          { bg: 'rgba(59,130,246,.12)',       text: '#3b82f6',             label: 'Module+' },
  MODULE_UPDATED:          { bg: 'rgba(59,130,246,.12)',       text: '#3b82f6',             label: 'Module~' },
  MODULE_DELETED:          { bg: 'rgba(239,68,68,.12)',        text: '#ef4444',             label: 'Module✕' },
  SUBMODULE_CREATED:       { bg: 'rgba(6,182,212,.12)',        text: '#06b6d4',             label: 'SubMod+' },
  SUBMODULE_UPDATED:       { bg: 'rgba(6,182,212,.12)',        text: '#06b6d4',             label: 'SubMod~' },
  SUBMODULE_DELETED:       { bg: 'rgba(239,68,68,.12)',        text: '#ef4444',             label: 'SubMod✕' },
  SCREEN_CREATED:          { bg: 'rgba(20,184,166,.12)',       text: '#14b8a6',             label: 'Screen+' },
  SCREEN_UPDATED:          { bg: 'rgba(20,184,166,.12)',       text: '#14b8a6',             label: 'Screen~' },
  SCREEN_DELETED:          { bg: 'rgba(239,68,68,.12)',        text: '#ef4444',             label: 'Screen✕' },
  TIER_PRIVILEGES_SAVED:   { bg: 'rgba(251,146,60,.12)',       text: '#fb923c',             label: 'Tier Saved' },
  UAMS_PROPAGATED:         { bg: 'rgba(99,102,241,.12)',       text: '#6366f1',             label: 'Propagated' },
};

const ACTION_GROUPS = [
  { id: '',                 label: 'All' },
  { id: 'LOGIN',            label: 'Auth' },
  { id: 'SIGNUP_INITIATED', label: 'Signup' },
  { id: 'ACCOUNT_ACTIVATED',label: 'Activation' },
  { id: 'LICENSE_GENERATED',label: 'License' },
  { id: 'DOWNLOAD_WEBPAGE_PACK', label: 'Downloads' },
  { id: 'SESSION_FORCE_LOGOUT',  label: 'Sessions' },
  { id: 'TENANT_PASSWORD_RESET', label: 'Password' },
  { id: 'PRICING_TIER_CREATED',  label: 'Pricing' },
  { id: 'INDUSTRY_CREATED', label: 'Industry' },
  { id: 'MODULE_CREATED',   label: 'Modules' },
  { id: 'UAMS_PROPAGATED',  label: 'Sync' },
];

function ActionBadge({ action, success }) {
  const meta = ACTION_COLORS[action] || { bg: 'rgba(100,100,100,.1)', text: 'var(--text-secondary)', label: action };
  return (
    <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: '0.7rem', fontWeight: 600,
      background: meta.bg, color: meta.text, whiteSpace: 'nowrap', letterSpacing: '.02em' }}>
      {success === false ? <XCircle size={10} style={{ marginRight: 3, verticalAlign: 'middle' }} /> : null}
      {meta.label || action}
    </span>
  );
}

export default function UserAuditPage() {
  const [rows,       setRows]       = useState([]);
  const [stats,      setStats]      = useState(null);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  // Filters
  const [search,    setSearch]    = useState('');
  const [actionFlt, setActionFlt] = useState('');
  const [successFl, setSuccessFl] = useState('');
  const [dateFrom,  setDateFrom]  = useState('');
  const [dateTo,    setDateTo]    = useState('');

  const PAGE_SIZE = 50;

  const token = useMemo(() =>
    sessionStorage.getItem('erp_security_token') || localStorage.getItem('erp_security_token'), []);

  const headers = useMemo(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  // ── load stats ──
  const loadStats = useCallback(async () => {
    try {
      const r = await fetch(`${API}/user-audit/stats`, { headers });
      if (r.ok) setStats(await r.json());
    } catch (e) { console.error('[UserAudit] Stats fetch failed:', e.message); }
  }, [headers]);

  // ── load logs ──
  const loadLogs = useCallback(async (p = 1) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page: p, pageSize: PAGE_SIZE });
      if (search)    params.set('username',  search);
      if (actionFlt) params.set('action',    actionFlt);
      if (successFl) params.set('success',   successFl);
      if (dateFrom)  params.set('dateFrom',  dateFrom);
      if (dateTo)    params.set('dateTo',    dateTo + 'T23:59:59');

      const r = await fetch(`${API}/user-audit/logs?${params}`, { headers });
      if (!r.ok) throw new Error((await r.json()).error || r.statusText);
      const d = await r.json();
      setRows(d.rows || []);
      setTotal(d.total || 0);
      setTotalPages(d.totalPages || 1);
      setPage(p);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [headers, search, actionFlt, successFl, dateFrom, dateTo]);

  useEffect(() => {
    loadStats();
    loadLogs(1);
  }, [loadStats, loadLogs]);

  // ── export CSV ──
  const exportCSV = () => {
    const hdr = ['LogId','LogTime','Username','Role','ClientId','Action','EntityType','EntityId','IPAddress','Success','ErrorMsg'];
    const csv = [hdr.join(','), ...rows.map(r =>
      hdr.map(k => `"${(r[k] ?? '').toString().replace(/"/g, '""')}"`).join(',')
    )].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `user_audit_${Date.now()}.csv`;
    a.click();
  };

  const applyFilter = () => loadLogs(1);
  const resetFilter = () => { setSearch(''); setActionFlt(''); setSuccessFl(''); setDateFrom(''); setDateTo(''); };

  const fmtTime = iso => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit', second:'2-digit' });
  };

  return (
    <div data-testid="user-audit-page"
      style={{ display:'flex', flexDirection:'column', height:'calc(100vh - 200px)', minHeight:600 }}>

      {/* ── Header ── */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
        padding:'16px 20px 12px', borderBottom:'1px solid var(--border)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <Shield size={20} color="var(--accent-blue)" />
          <span style={{ fontSize:'1.05rem', fontWeight:700, color:'var(--text-primary)' }}>User Audit Trail</span>
          <span style={{ fontSize:'0.75rem', color:'var(--text-muted)',
            background:'var(--surface-hover)', padding:'2px 8px', borderRadius:20 }}>
            {total.toLocaleString()} entries
          </span>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <Button variant="outline" size="sm" onClick={() => { loadStats(); loadLogs(page); }} data-testid="refresh-audit">
            <RefreshCw size={13} style={{ marginRight:4 }} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-csv">
            <Download size={13} style={{ marginRight:4 }} /> Export CSV
          </Button>
        </div>
      </div>

      {/* ── Stats Banner ── */}
      {stats && (
        <div style={{ display:'flex', gap:12, padding:'12px 20px', borderBottom:'1px solid var(--border)',
          background:'var(--surface-hover)', flexWrap:'wrap' }}>
          {[
            { label:'Total Events',   val: stats.total,       icon: Activity,        color:'var(--accent-blue)' },
            { label:'Last 24 Hours',  val: stats.last24h,     icon: Clock,           color:'#22c55e' },
            { label:'Failed Actions', val: stats.failures,    icon: AlertTriangle,   color:'#ef4444' },
            { label:'Unique Users',   val: stats.uniqueUsers, icon: Users,           color:'#a855f7' },
          ].map(s => (
            <div key={s.label} style={{ display:'flex', alignItems:'center', gap:8,
              background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8,
              padding:'8px 14px', minWidth:140 }}>
              <s.icon size={16} color={s.color} />
              <div>
                <div style={{ fontSize:'1.1rem', fontWeight:700, color:'var(--text-primary)', lineHeight:1 }}>{s.val?.toLocaleString()}</div>
                <div style={{ fontSize:'0.68rem', color:'var(--text-muted)' }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Group Quick-Filters ── */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:6, padding:'8px 20px 0',
        borderBottom:'none', alignItems:'center' }}>
        <span style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginRight:2 }}>Quick filter:</span>
        {ACTION_GROUPS.map(g => {
          const active = g.id === '' ? actionFlt === '' : actionFlt.startsWith(g.id.split('_')[0]) || g.id === actionFlt;
          return (
            <button key={g.id} data-testid={`group-filter-${g.id||'all'}`}
              onClick={() => setActionFlt(g.id)}
              style={{
                padding:'3px 10px', borderRadius:20, fontSize:'0.7rem', fontWeight:600, cursor:'pointer',
                border: active ? '1.5px solid var(--accent-blue)' : '1px solid var(--border)',
                background: active ? 'var(--accent-blue-soft)' : 'var(--surface)',
                color: active ? 'var(--accent-blue)' : 'var(--text-secondary)',
                transition:'all .15s',
              }}>
              {g.label}
            </button>
          );
        })}
      </div>

      {/* ── Filters ── */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:8, padding:'10px 20px',
        borderBottom:'1px solid var(--border)', alignItems:'center' }}>
        <div style={{ position:'relative', flex:'0 0 200px' }}>
          <Search size={13} style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)' }} />
          <Input placeholder="Search username..." value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && applyFilter()}
            style={{ paddingLeft:28, height:32, fontSize:'0.8rem' }} data-testid="audit-search-user" />
        </div>
        <select value={actionFlt} onChange={e => setActionFlt(e.target.value)}
          style={{ height:32, fontSize:'0.8rem', border:'1px solid var(--border)', borderRadius:6,
            background:'var(--surface)', color:'var(--text-primary)', padding:'0 8px', cursor:'pointer' }}
          data-testid="audit-filter-action">
          <option value="">All Actions</option>
          {Object.keys(ACTION_COLORS).map(a => <option key={a} value={a}>{ACTION_COLORS[a].label || a}</option>)}
        </select>
        <select value={successFl} onChange={e => setSuccessFl(e.target.value)}
          style={{ height:32, fontSize:'0.8rem', border:'1px solid var(--border)', borderRadius:6,
            background:'var(--surface)', color:'var(--text-primary)', padding:'0 8px', cursor:'pointer' }}>
          <option value="">Success / Fail</option>
          <option value="1">Success only</option>
          <option value="0">Failures only</option>
        </select>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          style={{ height:32, fontSize:'0.8rem', border:'1px solid var(--border)', borderRadius:6,
            background:'var(--surface)', color:'var(--text-primary)', padding:'0 8px', cursor:'pointer' }} />
        <span style={{ color:'var(--text-muted)', fontSize:'0.8rem' }}>to</span>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          style={{ height:32, fontSize:'0.8rem', border:'1px solid var(--border)', borderRadius:6,
            background:'var(--surface)', color:'var(--text-primary)', padding:'0 8px', cursor:'pointer' }} />
        <Button size="sm" onClick={applyFilter} data-testid="apply-audit-filter"
          style={{ height:32 }}>
          <Filter size={12} style={{ marginRight:4 }} /> Apply
        </Button>
        <Button size="sm" variant="ghost" onClick={resetFilter} style={{ height:32, fontSize:'0.78rem' }}>
          Clear
        </Button>
      </div>

      {/* ── Table ── */}
      {error ? (
        <div style={{ padding:24, color:'#ef4444', textAlign:'center', fontSize:'0.88rem' }}>
          <AlertTriangle size={16} style={{ marginRight:6 }} />{error}
        </div>
      ) : loading ? (
        <div style={{ padding:40, textAlign:'center', color:'var(--text-muted)', fontSize:'0.85rem' }}>Loading audit log…</div>
      ) : (
        <div style={{ flex:1, overflow:'auto', minHeight:350 }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.8rem' }}>
            <thead>
              <tr style={{ background:'var(--surface-hover)', position:'sticky', top:0, zIndex:5 }}>
                {['Time','User','Role','Client','Action','Entity Type','Entity ID','IP','Status'].map(h => (
                  <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:600,
                    color:'var(--text-muted)', borderBottom:'1px solid var(--border)', whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={9} style={{ padding:32, textAlign:'center', color:'var(--text-muted)' }}>No audit log entries found.</td></tr>
              ) : rows.map(r => (
                <tr key={r.LogId} data-testid={`audit-row-${r.LogId}`}
                  style={{ borderBottom:'1px solid var(--border)', transition:'background .15s' }}
                  onMouseEnter={e => e.currentTarget.style.background='var(--surface-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background=''}>
                  <td style={{ padding:'7px 12px', whiteSpace:'nowrap', color:'var(--text-secondary)', fontSize:'0.75rem' }}>
                    {fmtTime(r.LogTime)}
                  </td>
                  <td style={{ padding:'7px 12px', fontWeight:600, color:'var(--text-primary)' }}>
                    {r.Username || '—'}
                  </td>
                  <td style={{ padding:'7px 12px', color:'var(--text-secondary)' }}>
                    {r.Role || '—'}
                  </td>
                  <td style={{ padding:'7px 12px', color:'var(--text-muted)', fontSize:'0.75rem' }}>
                    {r.ClientId || '—'}
                  </td>
                  <td style={{ padding:'7px 12px' }}>
                    <ActionBadge action={r.Action} success={r.Success !== 0} />
                  </td>
                  <td style={{ padding:'7px 12px', color:'var(--text-secondary)' }}>
                    {r.EntityType || '—'}
                  </td>
                  <td style={{ padding:'7px 12px', color:'var(--text-muted)', fontSize:'0.75rem', maxWidth:120, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {r.EntityId || '—'}
                  </td>
                  <td style={{ padding:'7px 12px', color:'var(--text-muted)', fontSize:'0.72rem' }}>
                    {r.IPAddress || '—'}
                  </td>
                  <td style={{ padding:'7px 12px' }}>
                    {r.Success !== 0
                      ? <CheckCircle size={14} color="#22c55e" />
                      : <XCircle    size={14} color="#ef4444" title={r.ErrorMsg || ''} />
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'8px 20px', borderTop:'1px solid var(--border)', background:'var(--surface-hover)' }}>
          <span style={{ fontSize:'0.78rem', color:'var(--text-muted)' }}>
            Page {page} of {totalPages} • {total.toLocaleString()} total entries
          </span>
          <div style={{ display:'flex', gap:6 }}>
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => loadLogs(page - 1)} style={{ height:28, fontSize:'0.78rem' }}>Prev</Button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const start = Math.max(1, Math.min(page - 2, totalPages - 4));
              const p     = start + i;
              return (
                <Button key={p} size="sm" variant={p === page ? 'default' : 'outline'}
                  onClick={() => loadLogs(p)} style={{ height:28, minWidth:28, fontSize:'0.78rem', padding:'0 8px' }}>{p}</Button>
              );
            })}
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => loadLogs(page + 1)} style={{ height:28, fontSize:'0.78rem' }}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
